import { consume } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../design-system/button/button';
import '../../design-system/modal/modal';
import { i18n } from '../../i18n/i18n';
import { Logger, loggerContext } from '../../providers/logger.context';
import resetStyle from '../../shared/components/html-content/reset.style';
import '../../shared/components/social-links/social-links';
import '../../shared/components/svg-image/svg-image';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsSpacing } from '../../shared/styles/spacing';
import { ludsBaseFont, ludsHeadingXS, ludsTypography, ludsTypographyFontWeight } from '../../shared/styles/typography';
import { ENROLLMENT_STATUSES, EntryComponentConfig } from '../../shared/types/common.model';
import './completion-item';
import type { CompletionItem } from './completion-item';
import { CoursePlayerState, stateContext } from './course-player.context';
import '../share-certificate/share-certificate-view';
import { configContext } from '../../providers/config.context';

@customElement('lua-course-player-complete')
export class LuaCoursePlayerComplete extends LitElement {
  static override styles = [
    resetStyle,
    overrideDesignTokens,
    ludsBaseFont,
    ludsTypography,
    ludsTypographyFontWeight,
    ludsHeadingXS,
    ludsSpacing,

    css`
      :host {
        --luds-dialog-body-spacing: var(--spacing-spacing-24);
      }
      .container {
        text-align: center;
        padding: var(--spacing-spacing-32);
        padding-top: var(--spacing-spacing-24);
        border-radius: var(--corners-l);
        background: var(--semantic-colour-tokens-surface-surface-secondary);
      }

      ul {
        list-style-type: none;
        padding: 0;
        margin: 0;
      }

      ul.actions {
        margin-bottom: var(--spacing-spacing-24);
        background: var(--semantic-colour-tokens-surface-surface-primary);
        border: 1px solid var(--semantic-colour-tokens-stroke-stroke-primary);
        border-radius: var(--corners-m);

        li {
          padding: var(--spacing-spacing-16);
          border-top: 1px solid var(--semantic-colour-tokens-stroke-stroke-primary);
        }

        li:first-child {
          border-top: none;
        }
      }
    `,
  ];

  @consume({ context: configContext })
  private internalConfig: EntryComponentConfig;

  @consume({ context: stateContext, subscribe: true })
  coursePlayerState: CoursePlayerState;

  @consume({ context: loggerContext })
  @property({ attribute: false })
  logger: Logger;

  private closeShareModal() {
    this.coursePlayerState.closeSocialLinksModal?.();
  }

  private exitCourse() {
    this.coursePlayerState.exitCourse?.();
  }

  private launchNextCourse() {
    this.coursePlayerState.navigateToNextCourse?.();
  }

  private back() {
    location.reload();
  }

  private getActionsByStatus() {
    if (this.coursePlayerState.nextCourseOnPath) {
      return html`
        <luds-button variant="secondary" data-qa-id="Exit course" @click=${this.exitCourse}
          >${i18n('lua.courses.completion.actions.exit_course')}</luds-button
        >
        <luds-button variant="primary" data-qa-id="Launch new course" @click=${this.launchNextCourse}
          >${i18n('lua.courses.completion.actions.new_course')}</luds-button
        >
      `;
    }

    return html`
      <luds-button variant="primary" data-qa-id="Exit course" @click=${this.exitCourse}
        >${i18n('lua.courses.completion.actions.exit_course')}</luds-button
      >
    `;
  }

  private getViewLearningJourneyButton() {
    return html`
      <luds-button
        variant="secondary"
        data-qa-id="View Learning Journey"
        @click=${() => this.coursePlayerState.navigateToLearningJourney()}
        >${i18n('lua.courses.completion.actions.view_learning_journey')}</luds-button
      >
    `;
  }

  /**
   * Returns an array of completion items (async + static) without duplicates,
   * using title+icon as the unique key.
   */
  private getAllCompletionItems(): CompletionItem[] {
    const staticItems: CompletionItem[] = this.coursePlayerState.getCompletionItems?.() || [];
    const asyncItems: CompletionItem[] = this.coursePlayerState.completePage.items || [];
    const seen = new Set<string>();
    return [...asyncItems, ...staticItems].filter((item) => {
      const key = (item.title ?? '') + (item.icon ?? '');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  private renderSuccess() {
    const items = this.getAllCompletionItems();

    return html`<section class="container" data-testid="lua-passed-course">
      <lua-svg-image name="congratulations" width="104" height="104"></lua-svg-image>
      <h1 class="mb-32 fw-600">${i18n('lua.courses.completion.success.title')}</h1>

      ${items.length > 0
        ? html` <ul class="actions">
            ${items.map((item) => {
              return html`
                <li>
                  <lua-completion-item
                    data-list-item
                    .itemTitle="${item.title}"
                    .description="${item.description}"
                    .icon="${item.icon}"
                    .buttons="${item.buttons ?? []}"></lua-completion-item>
                </li>
              `;
            })}
          </ul>`
        : nothing}
      ${this.coursePlayerState?.learningJourneyContext ? html` ${this.getViewLearningJourneyButton()} ` : nothing}
      ${this.coursePlayerState.showExitCourseButton ? html` ${this.getActionsByStatus()} ` : nothing}
      ${this.coursePlayerState.completePage.social_sharing?.linkedin_is_enabled ||
      this.coursePlayerState.completePage.social_sharing?.twitter_is_enabled
        ? html`<lua-share-certificate
            .open=${Boolean(this.coursePlayerState.completePage.isShareModalOpen)}
            @close-certificate=${this.closeShareModal}
            enrollmentId=${this.coursePlayerState.currentModule.enrollment_id}
            .linkedinEnabled=${!!this.coursePlayerState.completePage.social_sharing?.linkedin_is_enabled}
            .twitterEnabled=${!!this.coursePlayerState.completePage.social_sharing?.twitter_is_enabled}
            .twitterData=${{
              twitter_hash_tags: this.coursePlayerState.completePage.social_sharing?.twitter_hash_tags,
              twitter_related_accounts: this.coursePlayerState.completePage.social_sharing?.twitter_related_accounts,
              course_name: this.coursePlayerState.enrollment.name,
            }}></lua-share-certificate>`
        : nothing}
    </section>`;
  }

  private renderFailure() {
    const descriptionKey = this.canReAttempt()
      ? 'lua.courses.completion.failure.description_reattempt'
      : 'lua.courses.completion.failure.description';

    return html`<section class="container" data-testid="lua-failed-course">
      <lua-svg-image name="trouble" width="104" height="104"></lua-svg-image>
      <h1 class="mb-8 fw-600">${i18n('lua.courses.completion.failure.title')}</h1>
      <p class="mb-24">${i18n(descriptionKey)}</p>

      ${this.coursePlayerState.showExitCourseButton
        ? html` <luds-button variant="secondary" data-qa-id="Back" @click=${this.back}
              >${i18n('lua.courses.completion.back')}</luds-button
            >
            <luds-button variant="primary" data-qa-id="Exit course" @click=${this.exitCourse}
              >${i18n('lua.courses.completion.actions.exit_course')}</luds-button
            >`
        : nothing}
    </section>`;
  }

  private isCourseCompletionSuccessful() {
    return [ENROLLMENT_STATUSES.PASSED, ENROLLMENT_STATUSES.COMPLETED].some(
      (status) => status === this.coursePlayerState?.enrollment?.status_id
    );
  }

  private canReAttempt() {
    return this.coursePlayerState.enrollment.has_attempts_remaining;
  }

  protected override render() {
    if (this.isCourseCompletionSuccessful()) {
      return this.renderSuccess();
    }

    return this.renderFailure();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-course-player-complete': LuaCoursePlayerComplete;
  }
}
